/**
 * Saved views for the invoice list — AC98.
 *
 * A "view" is a named, resurrectable snapshot of the toolbar's
 * filter state:
 *   - status tab (all/draft/sent/overdue/paid/voided/reversed)
 *   - quick filter chip
 *   - search string
 *   - customer id
 *   - date-from / date-to
 *   - amount min / max
 *
 * Persistence: `localStorage`, keyed per company + user, so multi-
 * tenant machines don't leak view names between workspaces. Storing
 * per-user (rather than in Postgres) keeps the round-trip cost at
 * zero and matches how Notion / Pipedrive persist "my saved views"
 * — cross-device sync is an explicit follow-on for V1.x if needed.
 *
 * View names are trimmed + unique per (company, user); duplicate
 * names overwrite the older entry. `Delete` is exposed via the
 * dropdown so the operator can always clean up their own list.
 */
import type { StatusFilter, QuickFilter } from "./index.ts";

export interface SavedInvoiceView {
  id: string;
  name: string;
  status: StatusFilter;
  quick: QuickFilter;
  search: string;
  customerId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  amountMin: string | null;
  amountMax: string | null;
  createdAt: string;
}

const KEY_PREFIX = "flint:invoices:saved-views";

function storageKey(companyId: string, userId: string): string {
  return `${KEY_PREFIX}:${companyId}:${userId}`;
}

export function loadSavedViews(companyId: string, userId: string): SavedInvoiceView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(companyId, userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedView);
  } catch {
    return [];
  }
}

function isSavedView(v: unknown): v is SavedInvoiceView {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.name === "string" &&
    typeof o.status === "string" &&
    (typeof o.search === "string" || o.search === "")
  );
}

function persist(companyId: string, userId: string, views: SavedInvoiceView[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(companyId, userId), JSON.stringify(views));
}

export interface CurrentViewState {
  status: StatusFilter;
  quick: QuickFilter;
  search: string;
  customerId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  amountMin: string | null;
  amountMax: string | null;
}

export function saveView(
  companyId: string,
  userId: string,
  name: string,
  current: CurrentViewState,
): SavedInvoiceView[] {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("View name is required.");
  const existing = loadSavedViews(companyId, userId);
  // Overwrite by name (case-insensitive) so re-saving the same view
  // updates it rather than piling up duplicates.
  const withoutDupe = existing.filter(
    (v) => v.name.toLowerCase() !== trimmed.toLowerCase(),
  );
  const next: SavedInvoiceView = {
    id: `sv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    name: trimmed,
    createdAt: new Date().toISOString(),
    ...current,
  };
  const updated = [next, ...withoutDupe];
  persist(companyId, userId, updated);
  return updated;
}

export function deleteView(
  companyId: string,
  userId: string,
  viewId: string,
): SavedInvoiceView[] {
  const existing = loadSavedViews(companyId, userId);
  const updated = existing.filter((v) => v.id !== viewId);
  persist(companyId, userId, updated);
  return updated;
}
